import React, { useEffect, useState } from 'react';
import { FileText, CheckCircle, Eye, X, Save, Book, Settings, Info, GripVertical, Check, ChevronUp } from 'lucide-react';
import useAssessmentManagementStore from '@/stores/AdminStore/AssessmentManagementStore';
import {
  AvailableQuestion,
  createAssessment,
  updateAssessment,
  getAssessmentById,
} from '@/services/PagesServices/AssessmentManagementService';
import AssessmentPreviewModal from '@/pages/_shared/AssessmentPreviewModal';
import './CreateAssessment.css';

interface CreateAssessmentProps {
  assessmentId?: number;
  seedQuestions?: AvailableQuestion[];
  onCancel?: () => void;
  onSaved?: () => void;
  onSeedConsumed?: () => void;
}

const CreateAssessment: React.FC<CreateAssessmentProps> = ({
  assessmentId,
  seedQuestions,
  onCancel,
  onSaved,
  onSeedConsumed,
}) => {
  const { availableQuestions } = useAssessmentManagementStore();
  const [submitting, setSubmitting] = useState<null | 'draft' | 'published'>(null);
  const [loadingExisting, setLoadingExisting] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('');
  const [timeLimit, setTimeLimit] = useState('60');
  const [passingScore, setPassingScore] = useState('70');
  const [passingScoreEnabled, setPassingScoreEnabled] = useState(true);
  const [attemptsAllowed, setAttemptsAllowed] = useState('3');
  const [attemptsLimitEnabled, setAttemptsLimitEnabled] = useState(true);
  const [randomizeQuestions, setRandomizeQuestions] = useState(true);
  const [immediateFeedback, setImmediateFeedback] = useState(true);
  const [selectedQuestions, setSelectedQuestions] = useState<AvailableQuestion[]>([]);
  const [showQuestionBank, setShowQuestionBank] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const toggleQuestionSelection = (question: AvailableQuestion) => {
    const exists = selectedQuestions.find(q => q.id === question.id);
    if (exists) {
      setSelectedQuestions(selectedQuestions.filter(q => q.id !== question.id));
    } else {
      setSelectedQuestions([...selectedQuestions, question]);
    }
  };

  const isQuestionSelected = (questionId: number) => {
    return selectedQuestions.some(q => q.id === questionId);
  };

  const removeQuestion = (questionId: number) => {
    setSelectedQuestions(selectedQuestions.filter(q => q.id !== questionId));
  };

  const totalPoints = selectedQuestions.reduce((sum, q) => sum + q.points, 0);
  const isEditing = assessmentId != null;

  // Seed the question list from a Carve Assessment hand-off (one-shot).
  useEffect(() => {
    if (!seedQuestions || seedQuestions.length === 0) return;
    if (assessmentId != null) return;
    setSelectedQuestions(seedQuestions);
    onSeedConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedQuestions, assessmentId]);

  useEffect(() => {
    if (assessmentId == null) return;
    let cancelled = false;
    setLoadingExisting(true);
    getAssessmentById(assessmentId).then((res) => {
      if (cancelled) return;
      if (res.success && res.data) {
        setTitle(res.data.title);
        setDescription(res.data.description);
        setImmediateFeedback(res.data.immediateFeedback);
        setSelectedQuestions(res.data.questions);

        if (res.data.timeLimit != null) setTimeLimit(String(res.data.timeLimit));
        if (res.data.passingScore != null) {
          setPassingScore(String(res.data.passingScore));
          setPassingScoreEnabled(true);
        } else {
          setPassingScoreEnabled(false);
        }
        if (res.data.attemptsAllowed != null) {
          setAttemptsAllowed(String(res.data.attemptsAllowed));
          setAttemptsLimitEnabled(true);
        } else {
          setAttemptsLimitEnabled(false);
        }
        setRandomizeQuestions(!!res.data.randomizeQuestions);
      } else {
        window.alert(res.error?.message || 'Failed to load assessment.');
      }
      setLoadingExisting(false);
    });
    return () => {
      cancelled = true;
    };
  }, [assessmentId]);

  const handleSubmit = async (status: 'draft' | 'published') => {
    if (submitting) return;

    if (!title.trim()) {
      window.alert('Please enter a title.');
      return;
    }
    if (status === 'published') {
      if (!type) {
        window.alert('Please choose an assessment type before publishing.');
        return;
      }
      if (selectedQuestions.length === 0) {
        window.alert('Add at least one question before publishing.');
        return;
      }
    }

    setSubmitting(status);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        type,
        timeLimit: timeLimit === '' ? null : Number(timeLimit),
        passingScore: passingScoreEnabled && passingScore !== '' ? Number(passingScore) : null,
        attemptsAllowed: attemptsLimitEnabled && attemptsAllowed !== '' ? Number(attemptsAllowed) : null,
        randomizeQuestions,
        immediateFeedback,
        status,
        questions: selectedQuestions.map((q) => ({
          id: q.id,
          type: q.type,
          question: q.question,
          points: q.points,
          options: q.options,
          correctAnswer: q.correctAnswer,
        })),
      };

      const result = isEditing
        ? await updateAssessment(assessmentId as number, payload)
        : await createAssessment(payload);

      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to save assessment.');
      }

      onSaved?.();
    } catch (err: any) {
      window.alert(err?.message || 'Failed to save assessment.');
    } finally {
      setSubmitting(null);
    }
  };

  const handleCancel = () => {
    if (!title.trim() && selectedQuestions.length === 0) {
      onCancel?.();
      return;
    }
    if (window.confirm('Discard this assessment? Unsaved changes will be lost.')) {
      onCancel?.();
    }
  };

  return (
    <div className="admin-assessment-create-assessment">
      <div className="admin-assessment-create-layout">
        {/* Main Content */}
        <div className="admin-assessment-create-main">

          {/* Assessment Information */}
          <div className="admin-assessment-create-section">
            <h3>Assessment Information</h3>

            <div className="admin-assessment-form-group">
              <label>Title *</label>
              <input
                type="text"
                placeholder="e.g., Python Programming Final Test"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="admin-assessment-form-input"
              />
            </div>

            <div className="admin-assessment-form-group">
              <label>Description</label>
              <textarea
                placeholder="Describe the assessment..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="admin-assessment-form-textarea"
              />
            </div>

            <div className="admin-assessment-settings-grid">
              <div className="admin-assessment-form-group">
                <label>Type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="admin-assessment-form-input"
                >
                  <option value="">Select</option>
                  <option value="quiz">Quiz</option>
                  <option value="test">Test</option>
                  <option value="survey">Survey</option>
                </select>
              </div>

              <div className="admin-assessment-form-group">
                <label>Time Limit (minutes)</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={timeLimit}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d+$/.test(v)) setTimeLimit(v);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault();
                  }}
                  className="admin-assessment-form-input"
                />
              </div>
            </div>
          </div>

          {/* Questions Section */}
          <div className="admin-assessment-create-section">
            <div className="admin-assessment-section-header">
              <h3>Questions ({selectedQuestions.length})</h3>
              <button
                className="admin-assessment-add-questions-btn"
                onClick={() => setShowModal(true)}
              >
                + Add Questions
              </button>
            </div>

            {/* Expandable Question Bank */}
            {showModal && (
              <div className="admin-assessment-question-bank-expanded">
                <div className="admin-assessment-question-bank-header">
                  <h4>Select from Question Bank</h4>
                  <button
                    className="admin-assessment-close-bank-btn"
                    onClick={() => setShowModal(false)}
                  >
                    <ChevronUp size={20} />
                  </button>
                </div>

                <div className="admin-assessment-available-questions-list">
                  {availableQuestions?.map((question) => (
                    <div
                      key={question.id}
                      className={`admin-assessment-bank-question-card ${isQuestionSelected(question.id) ? 'selected' : ''}`}
                      onClick={() => toggleQuestionSelection(question)}
                    >
                      <div className="admin-assessment-question-badges">
                        <span className="admin-assessment-type-badge-orange">{question.type}</span>
                        <span className={`admin-assessment-difficulty-badge-${question.difficulty}`}>
                          {question.difficulty}
                        </span>
                      </div>
                      <div className="admin-assessment-bank-question-text">{question.question}</div>
                      {isQuestionSelected(question.id) && (
                        <div className="admin-assessment-selected-check">
                          <Check size={20} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Questions List */}
            {selectedQuestions.length > 0 && (
              <div className="admin-assessment-selected-questions-list">
                {selectedQuestions.map((q, index) => (
                  <div key={q.id} className="admin-assessment-selected-question-card">
                    <div className="admin-assessment-question-index">{index + 1}.</div>
                    <div className="admin-assessment-selected-question-content">
                      <div className="admin-assessment-selected-question-meta">
                        <span className="admin-assessment-type-badge-orange">{q.type}</span>
                        <span className="admin-assessment-points-badge">{q.points} pts</span>
                      </div>
                      <div className="admin-assessment-selected-question-text">{q.question}</div>
                    </div>
                    <button
                      onClick={() => removeQuestion(q.id)}
                      className="admin-assessment-remove-selected-btn"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Assessment Settings */}
          <div className="admin-assessment-create-section">
            <h3>Assessment Settings</h3>

            <div className="admin-assessment-settings-grid">
              <div className="admin-assessment-form-group">
                <label>Passing Score (%)</label>
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 13, fontWeight: 400, cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={passingScoreEnabled}
                    onChange={(e) => setPassingScoreEnabled(e.target.checked)}
                  />
                  Require passing score
                </label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={passingScoreEnabled ? passingScore : ''}
                  placeholder={passingScoreEnabled ? '' : 'No passing score required'}
                  disabled={!passingScoreEnabled}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') return setPassingScore('');
                    if (!/^\d+$/.test(v)) return;
                    const n = Math.min(100, Math.max(0, Number(v)));
                    setPassingScore(String(n));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault();
                  }}
                  className="admin-assessment-form-input"
                />
              </div>

              <div className="admin-assessment-form-group">
                <label>Attempts Allowed</label>
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 13, fontWeight: 400, cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={attemptsLimitEnabled}
                    onChange={(e) => setAttemptsLimitEnabled(e.target.checked)}
                  />
                  Limit attempts
                </label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={attemptsLimitEnabled ? attemptsAllowed : ''}
                  placeholder={attemptsLimitEnabled ? '' : 'Unlimited'}
                  disabled={!attemptsLimitEnabled}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d+$/.test(v)) setAttemptsAllowed(v);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault();
                  }}
                  className="admin-assessment-form-input"
                />
              </div>
            </div>

            <div className="admin-assessment-toggle-settings">
              <div className="admin-assessment-toggle-item">
                <div className="admin-assessment-toggle-info">
                  <div className="admin-assessment-toggle-label">Randomize Questions</div>
                  <div className="admin-assessment-toggle-description">Questions appear in random order</div>
                </div>
                <label className="admin-assessment-toggle-switch">
                  <input
                    type="checkbox"
                    checked={randomizeQuestions}
                    onChange={(e) => setRandomizeQuestions(e.target.checked)}
                  />
                  <span className="admin-assessment-toggle-slider"></span>
                </label>
              </div>

              <div className="admin-assessment-toggle-item">
                <div className="admin-assessment-toggle-info">
                  <div className="admin-assessment-toggle-label">Immediate Feedback</div>
                  <div className="admin-assessment-toggle-description">Show correct answers after submission</div>
                </div>
                <label className="admin-assessment-toggle-switch">
                  <input
                    type="checkbox"
                    checked={immediateFeedback}
                    onChange={(e) => setImmediateFeedback(e.target.checked)}
                  />
                  <span className="admin-assessment-toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="admin-assessment-create-sidebar">
          {/* Actions Card */}
          <div className="admin-assessment-sidebar-section">
            <h4>Actions</h4>
            <button
              type="button"
              className="admin-assessment-sidebar-btn primary"
              onClick={() => handleSubmit('published')}
              disabled={submitting !== null || loadingExisting}
            >
              <CheckCircle size={18} />
              {submitting === 'published'
                ? isEditing
                  ? 'Updating…'
                  : 'Publishing…'
                : isEditing
                ? 'Update & Publish'
                : 'Publish'}
            </button>
            <button
              type="button"
              className="admin-assessment-sidebar-btn secondary"
              onClick={() => handleSubmit('draft')}
              disabled={submitting !== null || loadingExisting}
            >
              <Save size={18} />
              {submitting === 'draft' ? 'Saving…' : 'Save Draft'}
            </button>
            <button
              type="button"
              className="admin-assessment-sidebar-btn secondary"
              onClick={() => {
                if (!title.trim() && selectedQuestions.length === 0) {
                  window.alert('Add a title or at least one question before previewing.');
                  return;
                }
                setShowPreview(true);
              }}
              disabled={submitting !== null}
            >
              <Eye size={18} />
              Preview
            </button>
            <button
              type="button"
              className="admin-assessment-sidebar-btn secondary"
              onClick={handleCancel}
              disabled={submitting !== null}
            >
              <X size={18} />
              Cancel
            </button>
          </div>

          {/* Summary Card */}
          <div className="admin-assessment-sidebar-section">
            <h4>Summary</h4>
            <div className="admin-assessment-summary-item">
              <span className="admin-assessment-summary-label">Questions</span>
              <span className="admin-assessment-summary-value">{selectedQuestions.length}</span>
            </div>
            <div className="admin-assessment-summary-item">
              <span className="admin-assessment-summary-label">Total Points</span>
              <span className="admin-assessment-summary-value">{totalPoints}</span>
            </div>
            <div className="admin-assessment-summary-item">
              <span className="admin-assessment-summary-label">Time Limit</span>
              <span className="admin-assessment-summary-value">{timeLimit} min</span>
            </div>
            <div className="admin-assessment-summary-item">
              <span className="admin-assessment-summary-label">Passing Score</span>
              <span className="admin-assessment-summary-value">
                {passingScoreEnabled ? `${passingScore}%` : 'Not required'}
              </span>
            </div>
            <div className="admin-assessment-summary-item">
              <span className="admin-assessment-summary-label">Attempts</span>
              <span className="admin-assessment-summary-value">
                {attemptsLimitEnabled ? attemptsAllowed : 'Unlimited'}
              </span>
            </div>
          </div>

          {/* Quick Tips Card */}
          <div className="admin-assessment-sidebar-section tips">
            <h4>Quick Tips</h4>
            <ul>
              <li>Mix question types for variety</li>
              <li>Set appropriate time limits</li>
              <li>Use randomization for tests</li>
              <li>Review all questions before publishing</li>
            </ul>
          </div>
        </div>
      </div>

      {showPreview && (
        <AssessmentPreviewModal
          title={title}
          description={description}
          assessmentType={type}
          timeLimit={timeLimit}
          passingScore={passingScoreEnabled ? passingScore : null}
          attemptsAllowed={attemptsLimitEnabled ? attemptsAllowed : null}
          randomizeQuestions={randomizeQuestions}
          immediateFeedback={immediateFeedback}
          questions={selectedQuestions}
          totalPoints={totalPoints}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

export default CreateAssessment;